// Hợp đồng của notification-service (đặc tả docs/backend/services/notification-service.md): hộp "Việc cần xử lý"
// (tính lúc đọc trên bản chiếu sự kiện, lọc theo quyền của người gọi), mục vận hành, bảng tin thông báo cá nhân.
// notification-service KHÔNG phát integration event (EVENTS rỗng); chỉ đẩy gợi ý thời gian thực sau commit:
// Realtime.publish(tenantId, 'inbox', { kinds }, staffIds) và 'notifications' { unread }.
import { z } from 'zod';

// ---------- Bộ dựng schema (thông điệp tiếng Việt) ----------
const REQUIRED = 'Thiếu dữ liệu bắt buộc';
const msg = (message: string) => ({ errorMap: () => ({ message }) });
const str = (required = REQUIRED) => z.string({ required_error: required, invalid_type_error: 'Phải là chuỗi ký tự' });
const text = (max = 200) => str().trim().max(max, `Tối đa ${max} ký tự`);
const uuid = str().uuid('Mã định danh không hợp lệ');
const bool = z.boolean({ required_error: REQUIRED, invalid_type_error: 'Phải là đúng/sai' });
const hhmm = str().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Giờ dạng HH:mm');
const qInt = (min: number, max: number, def?: number) => {
  const base = z.coerce.number({ invalid_type_error: 'Phải là số' }).int('Phải là số nguyên').min(min, `Tối thiểu ${min}`).max(max, `Tối đa ${max}`);
  return def === undefined ? base.optional() : base.default(def);
};
const boolish = z.preprocess(
  (v) => (v === 'true' || v === '1' || v === true ? true : v === 'false' || v === '0' || v === false ? false : undefined),
  z.boolean().optional(),
);

// ---------- Hằng số ----------
/** Loại mục "Việc cần xử lý" (KIND_ORDER = thứ tự khai báo). Nhãn/biểu tượng/liên kết: đặc tả §2.1. */
export const INBOX_KINDS = [
  'sla_overdue',
  'pool_lead',
  'deposit_pending',
  'lock_expiring',
  'queue_priority',
  'task_overdue',
  'leave_pending',
  'installment_due',
  'protection_expiring',
  'join_request',
  'payout_draft',
  'ops.offboarding_stuck',
  'ops.deal_submission_stuck',
  'ops.unit_change_stuck',
  'ops.unit_sync_stuck',
  'ops.deal_sync_anomaly',
  'ops.deletion_stalled',
  'ops.provisioning_stalled',
  'ops.audit_integrity',
  'ops.offboarding_draft',
  'ops.accounts_sync',
] as const;
export type InboxKind = (typeof INBOX_KINDS)[number];
export const INBOX_SEVERITIES = ['bad', 'warn', 'violet', 'info'] as const;
export type InboxSeverity = (typeof INBOX_SEVERITIES)[number];
/** Ngưỡng cố định của prototype. */
export const LOCK_EXPIRING_MINUTES = 20;
export const INSTALLMENT_WINDOW_DAYS = 7;
export const PROTECTION_WINDOW_DAYS = 3;
export const NOTIFICATION_SEVERITIES = ['good', 'info', 'warn', 'bad'] as const;
export type NotificationSeverity = (typeof NOTIFICATION_SEVERITIES)[number];
/** Loại thông báo cá nhân (§6.3). */
export const NOTIFICATION_KINDS = [
  'deal.confirmed',
  'deal.rejected',
  'deal.submissionFailed',
  'deal.contracted',
  'deal.unitChanged',
  'deal.unitChangeFailed',
  'deal.cancelled',
  'booking.lockOverridden',
  'booking.priority',
  'booking.projectWithdrawn',
  'booking.unitWithdrawn',
  'crm.assigned',
  'crm.importDone',
  'crm.exportReady',
  'crm.exportFailed',
  'attendance.leaveDecided',
  'commission.payoutApproved',
  'commission.payslipReady',
  'commission.approvalFailed',
  'commission.clawback',
  'commission.carryForward',
  'inventory.deletionRejected',
  'hr.handoverReceived',
  'security.passwordChanged',
] as const;
export type NotificationKind = (typeof NOTIFICATION_KINDS)[number];
export const CHANNELS = ['in_app', 'web_push', 'email', 'zalo_oa', 'sms'] as const;
export type Channel = (typeof CHANNELS)[number];
/** Chủ đề thời gian thực (gateway SSE GET /api/stream). */
export const REALTIME_TOPICS = { inbox: 'inbox', notifications: 'notifications' } as const;

// ---------- Lệnh ----------
/** Chỉ mục vận hành (key 'ops:<id>') xác nhận được; mục khác tự hết khi việc được xử lý. */
export const acknowledgeInboxItemInput = z.object({ key: str('Thiếu mục').regex(/^ops:[0-9a-f-]{36}$/i, 'Mục này tự hết khi việc được xử lý') });
export const markNotificationsReadInput = z
  .object({ ids: z.array(uuid).max(200, 'Tối đa 200 thông báo').optional(), all: bool.optional() })
  .refine((v) => v.all === true || (v.ids?.length ?? 0) > 0, { message: 'Chọn thông báo cần đánh dấu đã đọc', path: ['ids'] });
export const updateNotificationPreferencesInput = z.object({
  prefs: z
    .array(z.object({ kind: text(60).min(1, 'Thiếu loại thông báo'), channel: z.enum(CHANNELS, msg('Kênh không hợp lệ')), enabled: bool }))
    .max(500, 'Tối đa 500 tuỳ chọn'),
  quietHours: z.object({ from: hhmm, to: hhmm }).nullish(),
});
export const registerPushSubscriptionInput = z.object({
  endpoint: str('Thiếu endpoint').url('Endpoint không hợp lệ').max(1000, 'Endpoint quá dài').refine((u) => u.startsWith('https://'), 'Endpoint phải dùng HTTPS'),
  keys: z.object({ p256dh: str().min(1, 'Thiếu khoá p256dh').max(200, 'Khoá không hợp lệ'), auth: str().min(1, 'Thiếu khoá auth').max(100, 'Khoá không hợp lệ') }),
  userAgent: text(300).optional(),
});
export const removePushSubscriptionInput = z.object({ id: uuid });

// ---------- Truy vấn ----------
/** limit vắng mặt = toàn bộ mục; Tổng quan dùng 7. total & badges luôn tính trên toàn bộ. */
export const getInboxInput = z.object({ limit: qInt(1, 200) });
export const getInboxBadgesInput = z.object({});
export const listNotificationsInput = z.object({ cursor: text(200).optional(), limit: qInt(1, 100, 30), unread: boolish });
export const getUnreadNotificationCountInput = z.object({});
export const getNotificationPreferencesInput = z.object({});

/** Tên RPC: notification.cmd.<Name> / notification.qry.<Name>. */
export const RPC = {
  cmd: ['AcknowledgeInboxItem', 'MarkNotificationsRead', 'UpdateNotificationPreferences', 'RegisterPushSubscription', 'RemovePushSubscription'],
  qry: ['GetInbox', 'GetInboxBadges', 'ListNotifications', 'GetUnreadNotificationCount', 'GetNotificationPreferences'],
} as const;

// ---------- DTO ----------
/** Khớp kiểu web widgets/app-shell InboxItem. */
export interface InboxItem {
  /** Khoá ổn định: 'sla_overdue' | 'lock_expiring:<unitId>' | 'ops:<opsId>' … */
  id: string;
  kind: InboxKind;
  /** Module đích (số trên menu). */
  module: string;
  sev: InboxSeverity;
  icon: string;
  title: string;
  sub?: string;
  count: number;
  to: string;
  search?: Record<string, unknown>;
  meta?: { until?: string; oldestAt?: string; acknowledgeable?: boolean };
}
export interface Inbox {
  serverTime: string;
  /** Σ count trên toàn bộ mục. */
  total: number;
  /** Số mục (toàn bộ). */
  count: number;
  /** Đã sắp (mức độ → KIND_ORDER), cắt theo limit. */
  items: InboxItem[];
  badges: Record<string, number>;
}
export interface InboxBadges {
  serverTime: string;
  total: number;
  badges: Record<string, number>;
}
export interface NotificationItem {
  id: string;
  kind: NotificationKind;
  severity: NotificationSeverity;
  title: string;
  body: string | null;
  link: { to: string; search?: Record<string, unknown> } | null;
  createdAt: string;
  readAt: string | null;
}
export interface NotificationList {
  items: NotificationItem[];
  nextCursor: string | null;
  unreadCount: number;
}
export interface UnreadNotificationCount {
  unread: number;
}
export interface NotificationPreferences {
  channels: { channel: Channel; available: boolean }[];
  prefs: { kind: string; channel: Channel; enabled: boolean }[];
  quietHours: { from: string; to: string } | null;
  devices: { id: string; userAgent: string | null; createdAt: string }[];
}

// ---------- Kết quả lệnh ----------
/** POST /inbox/items/:key/acknowledge — idempotent: mục đã đóng trả changed = false. */
export interface AcknowledgeInboxItemResult {
  key: string;
  closed: true;
  closeReason: 'resolved' | 'acknowledged' | 'expired';
  /** true = lần xác nhận này đóng mục. */
  changed: boolean;
}
/** POST /notifications/read — updated = số thông báo vừa chuyển sang đã đọc (đã đọc giữ readAt cũ). */
export interface MarkNotificationsReadResult {
  updated: number;
  unread: number;
}
export interface UpdateNotificationPreferencesResult {
  updated: number;
}
export interface RegisterPushSubscriptionResult {
  id: string;
  /** Số thiết bị cũ bị thay (≤ 10 thiết bị mỗi người). */
  replaced: number;
}
export interface RemovePushSubscriptionResult {
  removed: true;
}

// ---------- Sự kiện ----------
/** notification-service không phát integration event. */
export const EVENTS = {} as const;
export type EventType = never;
export type EventPayloads = Record<never, never>;
